<script>
import { useAlert } from 'dashboard/composables';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import HubAPI from 'dashboard/api/hub';
import ClaudiaMirrorInboxesAPI from 'dashboard/api/claudiaMirrorInboxes';

export default {
  components: {
    PageHeader,
  },
  data() {
    return {
      claudiaProjects: [],
      connectorProjects: [],
      selectedProject: null,
      selectedConnectorProject: null,
      isCreating: false,
      isLoadingConnectorProjects: false,
    };
  },
  computed: {
    mirroredProjectNames() {
      const inboxes = this.$store.getters['inboxes/getInboxes'] || [];
      return new Set(
        inboxes
          .filter(inbox => inbox.channel_type === 'Channel::Claudia')
          .map(inbox => inbox.claudia_project_name)
          .filter(Boolean)
      );
    },
    availableProjects() {
      return this.claudiaProjects.filter(
        name => !this.mirroredProjectNames.has(name)
      );
    },
  },
  watch: {
    // Connector options are scoped to the chosen claudia slug (connector
    // projectName). Reset the selection and reload whenever the project changes.
    selectedProject(name) {
      this.selectedConnectorProject = null;
      this.connectorProjects = [];
      if (name) {
        this.fetchConnectorProjects(name);
      } else {
        // No project selected: no fetch will resolve, so clear loading here.
        this.isLoadingConnectorProjects = false;
      }
    },
  },
  async mounted() {
    this.$store.dispatch('inboxes/get');
    try {
      const { data } = await HubAPI.getTenant();
      this.claudiaProjects = (data.claudia_projects || []).map(
        project => project.name
      );
    } catch (error) {
      useAlert(
        error?.response?.data?.error ||
          this.$t('INBOX_MGMT.ADD.CLAUDIA_CHANNEL.ERROR_MESSAGE')
      );
    }
  },
  methods: {
    // The field is optional and the backend degrades to mirror pending, so a
    // connector list failure must not block inbox creation.
    async fetchConnectorProjects(projectName) {
      this.isLoadingConnectorProjects = true;
      const { data } = await ClaudiaMirrorInboxesAPI.getConnectorProjects(
        projectName
      ).catch(() => ({ data: { projects: [] } }));
      // Drop a stale response if the operator already switched projects, so the
      // options never belong to a different slug than the one selected.
      if (projectName !== this.selectedProject) return;
      this.connectorProjects = data.projects || [];
      this.isLoadingConnectorProjects = false;
    },
    // A claudia slug can map to several connector projects (one per helpdesk
    // channel); surface the type so operators can tell them apart.
    connectorOptionLabel(project) {
      return project.helpdesk_type
        ? `${project.name} (${project.helpdesk_type})`
        : project.name;
    },
    // Maps known backend error codes to friendly copy; dynamic reasons
    // (collision_account_*, provision_failed_*) fall back to ERROR_MESSAGE.
    errorMessageFor(reason) {
      const key = {
        no_instance_id: 'NO_INSTANCE_ID',
        no_admin_inviter: 'NO_ADMIN_INVITER',
        project_taken: 'PROJECT_TAKEN',
      }[reason];
      return key
        ? this.$t(`INBOX_MGMT.ADD.CLAUDIA_CHANNEL.ERRORS.${key}`)
        : this.$t('INBOX_MGMT.ADD.CLAUDIA_CHANNEL.ERROR_MESSAGE');
    },
    async createChannel() {
      if (!this.selectedProject || this.isCreating) return;

      this.isCreating = true;
      try {
        const { data } = await ClaudiaMirrorInboxesAPI.createMirrorInbox({
          projectName: this.selectedProject,
          connectorProjectName: this.selectedConnectorProject,
        });
        const pending = data.mirror?.status !== 'activated';
        useAlert(
          pending
            ? this.$t('INBOX_MGMT.ADD.CLAUDIA_CHANNEL.PENDING_MESSAGE')
            : this.$t('INBOX_MGMT.ADD.CLAUDIA_CHANNEL.SUCCESS_MESSAGE')
        );
        await this.$store.dispatch('inboxes/get');
        router.replace({ name: 'settings_inbox_list' });
      } catch (error) {
        useAlert(this.errorMessageFor(error?.response?.data?.error));
      } finally {
        this.isCreating = false;
      }
    },
  },
};
</script>

<template>
  <div
    class="border border-slate-25 dark:border-slate-800/60 bg-white dark:bg-slate-900 h-full p-6 w-full max-w-full md:w-3/4 md:max-w-[75%] flex-shrink-0 flex-grow-0"
  >
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.CLAUDIA_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.CLAUDIA_CHANNEL.DESC')"
    />
    <form class="flex flex-wrap mx-0" @submit.prevent="createChannel()">
      <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
        <label>
          {{ $t('INBOX_MGMT.ADD.CLAUDIA_CHANNEL.PROJECT_LABEL') }}
          <select v-model="selectedProject">
            <option :value="null" disabled>
              {{ $t('INBOX_MGMT.ADD.CLAUDIA_CHANNEL.PROJECT_PLACEHOLDER') }}
            </option>
            <option
              v-for="project in availableProjects"
              :key="project"
              :value="project"
            >
              {{ project }}
            </option>
          </select>
        </label>
      </div>

      <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
        <label for="claudia_connector_project_select">
          {{ $t('INBOX_MGMT.ADD.CLAUDIA_CHANNEL.CONNECTOR_LABEL') }}
          <select
            id="claudia_connector_project_select"
            v-model="selectedConnectorProject"
            :disabled="!selectedProject || isLoadingConnectorProjects"
          >
            <option :value="null">
              {{ $t('INBOX_MGMT.ADD.CLAUDIA_CHANNEL.CONNECTOR_PLACEHOLDER') }}
            </option>
            <option
              v-for="project in connectorProjects"
              :key="project.name"
              :value="project.name"
            >
              {{ connectorOptionLabel(project) }}
            </option>
          </select>
        </label>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.CLAUDIA_CHANNEL.CONNECTOR_HELP') }}
        </p>
      </div>

      <div class="w-full">
        <woot-submit-button
          :loading="isCreating"
          :disabled="!selectedProject"
          :button-text="$t('INBOX_MGMT.ADD.CLAUDIA_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>
